/**
 * PreferencesSheet — the top-right "Seaded" control.
 *
 * Four preferences the user can tweak and persist:
 *   - uiLang          (content language)
 *   - workCountry     (where I'm looking for work)
 *   - serviceCountry  (where I request / offer services)
 *   - locationQuery   (optional default city / area)
 *
 * All preferences are stored in localStorage via <usePrefs />; see
 * /app/frontend/src/lib/usePrefs.jsx for the storage key & shape.
 */
import React, { useEffect, useState } from 'react';
import { Settings } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

import { useLanguage, COUNTRIES } from '@/lib/context';
import { usePrefs } from '@/lib/usePrefs';

// Compact per-language labels. We bypass `t()` here on purpose so the Settings
// UI works even before translations load, and can never show humanized keys.
const STRINGS = {
  et: {
    open: 'Seaded',
    title: 'Eelistused',
    desc: 'Vali keel ja riigid, mida kasutada otsingus ja kaardil.',
    uiLang: 'Riik (sisu keel)',
    workCountry: 'Töö riik',
    workCountryHelp: 'Kus ma tahan tööd teha',
    serviceCountry: 'Teenuse riik / piirkond',
    serviceCountryHelp: 'Kus ma otsin või pakun teenuseid',
    locationQuery: 'Asukoht (linn/piirkond)',
    locationHint: 'Nt Tallinn, Helsinki, Oslo',
    anyCountry: 'Kõik riigid',
    save: 'Salvesta',
    cancel: 'Tühista',
    reset: 'Lähtesta',
  },
  en: {
    open: 'Settings',
    title: 'Preferences',
    desc: 'Choose the language and countries used in search and on the map.',
    uiLang: 'Country (content language)',
    workCountry: 'Work country',
    workCountryHelp: 'Where I want to work',
    serviceCountry: 'Service country / region',
    serviceCountryHelp: 'Where I request or offer services',
    locationQuery: 'Location (city / area)',
    locationHint: 'e.g. Tallinn, Helsinki, Oslo',
    anyCountry: 'All countries',
    save: 'Save',
    cancel: 'Cancel',
    reset: 'Reset',
  },
  fi: {
    open: 'Asetukset',
    title: 'Asetukset',
    desc: 'Valitse kieli ja maat, joita käytetään haussa ja kartalla.',
    uiLang: 'Maa (sisällön kieli)',
    workCountry: 'Työmaa',
    workCountryHelp: 'Missä haluan työskennellä',
    serviceCountry: 'Palvelumaa / alue',
    serviceCountryHelp: 'Missä haen tai tarjoan palveluita',
    locationQuery: 'Sijainti (kaupunki / alue)',
    locationHint: 'esim. Tallinna, Helsinki, Oslo',
    anyCountry: 'Kaikki maat',
    save: 'Tallenna',
    cancel: 'Peruuta',
    reset: 'Nollaa',
  },
  ru: {
    open: 'Настройки',
    title: 'Предпочтения',
    desc: 'Выберите язык и страны для поиска и карты.',
    uiLang: 'Страна (язык контента)',
    workCountry: 'Страна работы',
    workCountryHelp: 'Где я хочу работать',
    serviceCountry: 'Страна / регион услуг',
    serviceCountryHelp: 'Где я ищу или предлагаю услуги',
    locationQuery: 'Местоположение (город/район)',
    locationHint: 'напр. Таллин, Хельсинки, Осло',
    anyCountry: 'Все страны',
    save: 'Сохранить',
    cancel: 'Отмена',
    reset: 'Сбросить',
  },
  nb: {
    open: 'Innstillinger',
    title: 'Innstillinger',
    desc: 'Velg språk og land som brukes i søk og på kartet.',
    uiLang: 'Språk',
    workCountry: 'Arbeidsland',
    workCountryHelp: 'Hvor jeg vil jobbe',
    serviceCountry: 'Tjenesteland / region',
    serviceCountryHelp: 'Hvor jeg søker eller tilbyr tjenester',
    locationQuery: 'Sted (by / område)',
    locationHint: 'f.eks. Oslo, Tallinn, Helsinki',
    anyCountry: 'Alle land',
    save: 'Lagre',
    cancel: 'Avbryt',
    reset: 'Nullstill',
  },
};

const UI_LANGS = [
  { code: 'et', flag: '🇪🇪', label: 'Eesti' },
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'fi', flag: '🇫🇮', label: 'Suomi' },
  { code: 'nb', flag: '🇳🇴', label: 'Norsk' },
  { code: 'ru', flag: '🇷🇺', label: 'Русский' },
];

export const PreferencesSheet = ({
  variant = 'icon',      // 'icon' | 'text' | 'menu-row'
  side = 'right',
  className = '',
  testId = 'nav-settings-trigger',
  // Only one instance (by default the desktop top-right icon) should react
  // to the global `keikka:open-prefs` event to avoid stacking multiple
  // sheets when several PreferencesSheet triggers are mounted at once.
  respondToGlobalOpen = false,
}) => {
  const { language, setLanguage } = useLanguage();
  const { prefs, updatePrefs, resetPrefs } = usePrefs();
  const s = STRINGS[language] || STRINGS.en;

  const [open, setOpen] = useState(false);
  // Draft state so user can Cancel without persisting.
  const [draft, setDraft] = useState(() => ({
    uiLang: prefs.uiLang || language,
    workCountry: prefs.workCountry || '',
    serviceCountry: prefs.serviceCountry || '',
    locationQuery: prefs.locationQuery || '',
  }));

  // Resync draft when user re-opens the sheet
  useEffect(() => {
    if (open) {
      setDraft({
        uiLang: prefs.uiLang || language,
        workCountry: prefs.workCountry || '',
        serviceCountry: prefs.serviceCountry || '',
        locationQuery: prefs.locationQuery || '',
      });
    }
  }, [open, prefs, language]);

  // Allow any component (e.g. the "Location detected" banner) to open the
  // sheet by dispatching a `keikka:open-prefs` CustomEvent. Only the
  // instance opted-in via `respondToGlobalOpen` will react, so multiple
  // triggers in the header don't stack.
  useEffect(() => {
    if (!respondToGlobalOpen) return undefined;
    const openHandler = () => setOpen(true);
    window.addEventListener('keikka:open-prefs', openHandler);
    return () => window.removeEventListener('keikka:open-prefs', openHandler);
  }, [respondToGlobalOpen]);

  const handleSave = () => {
    updatePrefs({
      uiLang: draft.uiLang,
      workCountry: draft.workCountry,
      serviceCountry: draft.serviceCountry,
      locationQuery: draft.locationQuery.trim(),
    });
    if (draft.uiLang && draft.uiLang !== language) {
      setLanguage(draft.uiLang);
    }
    setOpen(false);
  };

  const handleReset = () => {
    resetPrefs();
    setDraft({
      uiLang: language,
      workCountry: '',
      serviceCountry: '',
      locationQuery: '',
    });
  };

  const Trigger = (() => {
    if (variant === 'text') {
      return (
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition ${className}`}
          data-testid={testId}
        >
          <Settings className="h-4 w-4" />
          <span>{s.open}</span>
        </button>
      );
    }
    if (variant === 'menu-row') {
      return (
        <button
          type="button"
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 transition ${className}`}
          data-testid={testId}
        >
          <Settings className="h-4 w-4" />
          <span>{s.open}</span>
        </button>
      );
    }
    return (
      <button
        type="button"
        className={`inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition touch-manipulation ${className}`}
        aria-label={s.open}
        style={{ WebkitTapHighlightColor: 'transparent' }}
        data-testid={testId}
      >
        <Settings className="h-5 w-5 text-slate-700" />
      </button>
    );
  })();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{Trigger}</SheetTrigger>
      <SheetContent side={side} className="w-[92vw] sm:w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle data-testid="settings-title">{s.title}</SheetTitle>
          <p className="text-sm text-slate-500 mt-1">{s.desc}</p>
        </SheetHeader>

        <div className="mt-6 space-y-5">
          {/* UI language */}
          <div className="space-y-1.5">
            <Label htmlFor="pref-uiLang" className="text-sm font-medium text-slate-800">
              {s.uiLang}
            </Label>
            <Select
              value={draft.uiLang}
              onValueChange={(v) => setDraft((d) => ({ ...d, uiLang: v }))}
            >
              <SelectTrigger id="pref-uiLang" data-testid="pref-uiLang">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UI_LANGS.map((lng) => (
                  <SelectItem key={lng.code} value={lng.code} data-testid={`pref-uiLang-${lng.code}`}>
                    {lng.flag} {lng.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Work country */}
          <div className="space-y-1.5">
            <Label htmlFor="pref-workCountry" className="text-sm font-medium text-slate-800">
              {s.workCountry}
            </Label>
            <p className="text-xs text-slate-500 -mt-0.5">{s.workCountryHelp}</p>
            <Select
              value={draft.workCountry || '__any__'}
              onValueChange={(v) =>
                setDraft((d) => ({ ...d, workCountry: v === '__any__' ? '' : v }))
              }
            >
              <SelectTrigger id="pref-workCountry" data-testid="pref-workCountry">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__" data-testid="pref-workCountry-any">
                  {s.anyCountry}
                </SelectItem>
                {COUNTRIES.map((c) => (
                  <SelectItem
                    key={c.code}
                    value={c.code}
                    data-testid={`pref-workCountry-${c.code}`}
                  >
                    {c.flag} {c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Service country */}
          <div className="space-y-1.5">
            <Label htmlFor="pref-serviceCountry" className="text-sm font-medium text-slate-800">
              {s.serviceCountry}
            </Label>
            <p className="text-xs text-slate-500 -mt-0.5">{s.serviceCountryHelp}</p>
            <Select
              value={draft.serviceCountry || '__any__'}
              onValueChange={(v) =>
                setDraft((d) => ({ ...d, serviceCountry: v === '__any__' ? '' : v }))
              }
            >
              <SelectTrigger id="pref-serviceCountry" data-testid="pref-serviceCountry">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__" data-testid="pref-serviceCountry-any">
                  {s.anyCountry}
                </SelectItem>
                {COUNTRIES.map((c) => (
                  <SelectItem
                    key={c.code}
                    value={c.code}
                    data-testid={`pref-serviceCountry-${c.code}`}
                  >
                    {c.flag} {c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Location query */}
          <div className="space-y-1.5">
            <Label htmlFor="pref-locationQuery" className="text-sm font-medium text-slate-800">
              {s.locationQuery}
            </Label>
            <Input
              id="pref-locationQuery"
              data-testid="pref-locationQuery"
              placeholder={s.locationHint}
              value={draft.locationQuery}
              onChange={(e) => setDraft((d) => ({ ...d, locationQuery: e.target.value }))}
            />
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={handleReset}
            data-testid="pref-reset"
          >
            {s.reset}
          </Button>
          <div className="flex items-center gap-2">
            <SheetClose asChild>
              <Button variant="outline" size="sm" type="button" data-testid="pref-cancel">
                {s.cancel}
              </Button>
            </SheetClose>
            <Button
              size="sm"
              type="button"
              onClick={handleSave}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="pref-save"
            >
              {s.save}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PreferencesSheet;
